import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { VirtuosoGrid } from 'react-virtuoso';
import Modal from './Modal';
import Card from './Card';
import { fetchCardsByYear } from '../api/cards';
import { fetchSeriesCards } from '../api/series';
import LoadingSpinner from './LoadingSpinner';

const gridComponents = {
    List: React.forwardRef(({ className, ...props }, ref) => (
        <div {...props} ref={ref} className={`series-cards-modal__grid ${className || ''}`} />
    )),
    Item: ({ children, className, ...props }) => (
        <div {...props} className={`series-cards-modal__item ${className || ''}`}>
            {children}
        </div>
    ),
};
gridComponents.List.displayName = 'SeriesCardsGridList';

const SeriesCardsModal = ({ year, seriesID, seriesName, onClose }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scrollEl, setScrollEl] = useState(null);

    useEffect(() => {
        const fetch = seriesID ? fetchSeriesCards(seriesID) : fetchCardsByYear(year);
        fetch
            .then(data => setCards([...data].sort((a, b) => b.rating - a.rating)))
            .catch(() => setCards([]))
            .finally(() => setLoading(false));
    }, [year, seriesID]);

    const renderCard = useCallback((index) => {
        const card = cards[index];
        if (!card) return null;
        return <Card card={card} hoverFlip fallbackSeriesKey={year ? `${year}-base` : null} />;
    }, [cards, year]);

    const title = year ? `${seriesName} (${year})` : seriesName;

    return (
        <Modal onClose={onClose} title={title} className="series-cards-modal">
            <h3>{title} — {cards.length} cards</h3>
            {loading ? (
                <LoadingSpinner />
            ) : cards.length === 0 ? (
                <p>No cards found.</p>
            ) : (
                <div className="series-cards-modal__scroll" ref={setScrollEl}>
                    {scrollEl && (
                        <VirtuosoGrid
                            totalCount={cards.length}
                            overscan={400}
                            components={gridComponents}
                            itemContent={renderCard}
                            computeItemKey={index => cards[index]?.cardID ?? index}
                            customScrollParent={scrollEl}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
};

SeriesCardsModal.propTypes = {
    year: PropTypes.number,
    seriesID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    seriesName: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default SeriesCardsModal;
